import { useEffect, useMemo, useRef, useState } from 'react'
import { doc, onSnapshot, setDoc } from 'firebase/firestore'
import {
  fingerprintRatings,
  type CloudRecommendationItem,
  type CloudRecommendationsRecord,
} from '@govu/shared'
import { firestore } from '../firebase'
import { useAuth } from '../auth/AuthProvider'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'
import { useLibraryMovies, useLibrarySeries } from '../library/useLibrary'
import {
  fetchSimilarMovies,
  fetchSimilarSeries,
  type TmdbSimilarTitle,
} from '../tmdb/tmdb'

/**
 * "Recommended for you" — weighted recommender (v3) that runs
 * entirely in the user's browser.
 *
 * Algorithm (identical to the desktop `WeightedRecommendationService`):
 *   1. **Seeds** = rated titles where `userRating >= SEED_MIN_RATING`
 *      (6 by default). Low ratings (1–5) are ignored — the user
 *      actively disliked them and they shouldn't influence picks.
 *      Movies and series are seeded separately.
 *   2. For each seed, fetch TMDB /recommendations. Each list is
 *      cached in localStorage keyed on `(seedTmdbId, language)`, so
 *      adding a new rating only costs one fetch (the new seed); every
 *      other seed reuses its cached list.
 *   3. For each candidate across all seeds:
 *        - discard anything already in the library
 *        - discard anything with fewer than CANDIDATE_MIN_VOTES
 *          TMDB votes — prevents niche films with a handful of
 *          high ratings from floating to the top
 *        - points += seed.userRating ** 2   (quadratic weighting —
 *          a 10 contributes 100 points per similar, a 9 → 81, an 8
 *          → 64, a 7 → 49, a 6 → 36. A single 10-rated seed
 *          dominates multiple 7-rated seeds.)
 *   4. Sort candidates by total points desc, tie-break on TMDB
 *      vote_average desc, cap at MAX_RESULTS (30).
 *
 * Cross-device sharing:
 *   - The aggregate result lives in Firestore at
 *     `users/{uid}/library/recommendations` together with a
 *     fingerprint of the input rating set.
 *   - On mount this hook subscribes to that doc. If the cloud
 *     fingerprint matches the current local fingerprint, the cloud
 *     result is rendered immediately and the local compute is
 *     skipped — whichever device computed first does the work for
 *     both.
 *   - When the local fingerprint differs from the cloud's, the hook
 *     computes locally and uploads the new aggregate.
 *   - The fingerprint format is versioned (`v2:…`) so an algorithm
 *     change automatically invalidates older cloud aggregates.
 */

const MAX_RESULTS = 200
const SEED_MIN_RATING = 6
const CANDIDATE_MIN_VOTES = 1000
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days
// Cache prefix history:
//   v1 — original (no voteCount)
//   v2 — voteCount added to TmdbSimilarTitle shape
//   v3 — per-seed fetch widened from 1 page to 3 pages of
//        /recommendations (~60 items/seed instead of 20)
const CACHE_PREFIX = 'govu:recs:v3'

interface Seed {
  tmdbId: number
  userRating: number
}

export interface BasedOnRatingsResult {
  movies: TmdbSimilarTitle[]
  series: TmdbSimilarTitle[]
  loading: boolean
  basedOn: { movies: Seed[]; series: Seed[] }
}

export function useBasedOnRatings(): BasedOnRatingsResult {
  const { user } = useAuth()
  const { settings } = useUserSettings()
  const { entries: libraryMovies } = useLibraryMovies()
  const { entries: librarySeries } = useLibrarySeries()
  const [movies, setMovies] = useState<TmdbSimilarTitle[]>([])
  const [series, setSeries] = useState<TmdbSimilarTitle[]>([])
  const [loading, setLoading] = useState(false)

  // Track the fingerprint of whatever's currently in cloud so we can
  // skip a local compute when it matches. Null until the first
  // Firestore snapshot fires.
  const [cloudFingerprint, setCloudFingerprint] = useState<string | null>(null)
  // In-flight compute guard so two snapshot-triggered effects don't
  // race to upload the same aggregate.
  const computingRef = useRef(false)

  // Every rated title is a seed, sorted by rating desc (order is only
  // for the hash / debug subtitle — the aggregator doesn't care).
  const movieSeeds = useMemo(() => pickAllRatedSeeds(libraryMovies), [libraryMovies])
  const seriesSeeds = useMemo(() => pickAllRatedSeeds(librarySeries), [librarySeries])

  const localFingerprint = useMemo(
    () => fingerprintRatings({ movies: movieSeeds, series: seriesSeeds }),
    [movieSeeds, seriesSeeds]
  )

  // ──────────────────────────────────────────────────────────────────
  // Cloud subscription — keeps `cloudFingerprint`, `movies`, `series`
  // in sync with whatever the authoritative aggregate is.
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.uid) {
      setCloudFingerprint(null)
      return
    }
    const ref = doc(firestore, `users/${user.uid}/library/recommendations`)
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setCloudFingerprint(null)
          return
        }
        const data = snap.data() as CloudRecommendationsRecord
        setCloudFingerprint(data.fingerprint)
        // Hydrate movies/series straight from cloud when the cloud
        // aggregate is current — the local compute effect will no-op.
        setMovies(data.movies.map(fromCloud))
        setSeries(data.series.map(fromCloud))
      },
      (err) => {
        console.warn('[recs] cloud subscription failed', err)
      }
    )
    return unsub
  }, [user?.uid])

  // ──────────────────────────────────────────────────────────────────
  // Compute effect — runs only when local fingerprint differs from
  // cloud. On match, the cloud snapshot handler already populated
  // state, so we return early and don't touch TMDB.
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Falls back to the bundled developer key when the user hasn't supplied
    // their own. The recommendations themselves are based on the user's own
    // ratings stored in Firestore — a TMDB user *session* is not required;
    // only an API key for /recommendations lookups.
    const apiKey = getEffectiveTmdbApiKey(settings)
    if (!apiKey) {
      return
    }
    if (!user?.uid) {
      return
    }
    if (movieSeeds.length === 0 && seriesSeeds.length === 0) {
      setMovies([])
      setSeries([])
      return
    }
    // Cloud already has the aggregate for this rating set — render it
    // (done by the snapshot handler) and skip compute.
    if (cloudFingerprint === localFingerprint) {
      return
    }
    if (computingRef.current) {
      return
    }

    const language = settings?.tmdbPreferredLanguage || 'en'
    const libraryMovieIds = new Set(libraryMovies.map((m) => m.tmdbId))
    const librarySeriesIds = new Set(librarySeries.map((s) => s.tmdbId))

    let cancelled = false
    computingRef.current = true
    setLoading(true)

    ;(async () => {
      try {
        const [movieRecs, seriesRecs] = await Promise.all([
          aggregate(movieSeeds, 'movie', apiKey, language, libraryMovieIds),
          aggregate(seriesSeeds, 'tv', apiKey, language, librarySeriesIds),
        ])
        if (cancelled) return
        setMovies(movieRecs)
        setSeries(seriesRecs)

        // Upload the freshly computed aggregate so the other client
        // can skip its own compute next time it opens Home.
        try {
          const payload: CloudRecommendationsRecord = {
            fingerprint: localFingerprint,
            movies: movieRecs.map(toCloud),
            series: seriesRecs.map(toCloud),
            computedAt: new Date().toISOString(),
            computedBy: 'pwa',
            seedCounts: { movies: movieSeeds.length, series: seriesSeeds.length },
          }
          const ref = doc(firestore, `users/${user.uid}/library/recommendations`)
          await setDoc(ref, payload)
        } catch (err) {
          console.warn('[recs] upload failed', err)
        }
      } finally {
        if (!cancelled) setLoading(false)
        computingRef.current = false
      }
    })()

    return () => {
      cancelled = true
      computingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    settings?.tmdbApiKey,
    settings?.tmdbPreferredLanguage,
    user?.uid,
    localFingerprint,
    cloudFingerprint,
  ])

  return { movies, series, loading, basedOn: { movies: movieSeeds, series: seriesSeeds } }
}

// ────────────────────────────────────────────────────────────────────
// Cloud payload conversions
// ────────────────────────────────────────────────────────────────────

function toCloud(t: TmdbSimilarTitle): CloudRecommendationItem {
  return {
    tmdbId: t.tmdbId,
    title: t.title,
    year: t.year,
    posterUrl: t.posterUrl,
    rating: t.rating,
    voteCount: t.voteCount,
    mediaType: t.mediaType,
  }
}

function fromCloud(c: CloudRecommendationItem): TmdbSimilarTitle {
  return {
    tmdbId: c.tmdbId,
    title: c.title,
    year: c.year,
    posterUrl: c.posterUrl,
    rating: c.rating,
    voteCount: c.voteCount ?? 0,
    mediaType: c.mediaType,
  }
}

// ────────────────────────────────────────────────────────────────────
// Core aggregator
// ────────────────────────────────────────────────────────────────────

async function aggregate(
  seeds: Seed[],
  kind: 'movie' | 'tv',
  apiKey: string,
  language: string,
  excludeIds: Set<number>
): Promise<TmdbSimilarTitle[]> {
  if (seeds.length === 0) return []

  // Fetch similar lists in parallel (cache-first). The TMDB client's
  // concurrency limiter handles actual request pacing.
  const lists = await Promise.all(
    seeds.map((seed) => getCachedSimilars(seed.tmdbId, kind, apiKey, language))
  )

  const scores = new Map<number, { points: number; entry: TmdbSimilarTitle }>()
  for (let i = 0; i < seeds.length; i++) {
    const seed = seeds[i]
    const list = lists[i]
    // Quadratic weight — userRating² — so a 10 gives 100 per similar
    // while a 7 only gives 49, letting genuinely-loved seeds dominate
    // merely-liked ones in the final ranking.
    const weight = seed.userRating * seed.userRating
    for (const candidate of list) {
      if (excludeIds.has(candidate.tmdbId)) continue
      // Drop thin-vote oddities — TMDB /recommendations happily
      // surfaces niche films with a few hundred votes, and the
      // user doesn't want them in Top for You.
      if (candidate.voteCount < CANDIDATE_MIN_VOTES) continue
      const existing = scores.get(candidate.tmdbId)
      if (existing) {
        existing.points += weight
      } else {
        scores.set(candidate.tmdbId, {
          points: weight,
          entry: candidate,
        })
      }
    }
  }

  return [...scores.values()]
    .sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points
      // Tie-break on TMDB rating so higher-quality candidates surface first.
      return b.entry.rating - a.entry.rating
    })
    .slice(0, MAX_RESULTS)
    .map((e) => e.entry)
}

// ────────────────────────────────────────────────────────────────────
// Persistent per-seed similars cache (localStorage)
// ────────────────────────────────────────────────────────────────────

interface CacheEntry {
  items: TmdbSimilarTitle[]
  cachedAt: number
}

async function getCachedSimilars(
  seedId: number,
  kind: 'movie' | 'tv',
  apiKey: string,
  language: string
): Promise<TmdbSimilarTitle[]> {
  const key = cacheKey(seedId, kind, language)
  const cached = readCache(key)
  if (cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
    return cached.items
  }

  const items =
    kind === 'movie'
      ? await fetchSimilarMovies(seedId, apiKey, language)
      : await fetchSimilarSeries(seedId, apiKey, language)

  writeCache(key, { items, cachedAt: Date.now() })
  return items
}

function cacheKey(seedId: number, kind: 'movie' | 'tv', language: string): string {
  return `${CACHE_PREFIX}:${kind}:${seedId}:${language}`
}

function readCache(key: string): CacheEntry | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CacheEntry
    if (!parsed || !Array.isArray(parsed.items) || typeof parsed.cachedAt !== 'number') {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeCache(key: string, entry: CacheEntry): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, JSON.stringify(entry))
  } catch {
    // Quota exceeded / Safari private mode / etc. — swallow and carry on;
    // next mount just refetches.
  }
}

// ────────────────────────────────────────────────────────────────────
// Seed selection
// ────────────────────────────────────────────────────────────────────

interface HasUserRating {
  tmdbId: number
  record: { userRating?: number | null }
}

function pickAllRatedSeeds(entries: HasUserRating[]): Seed[] {
  return entries
    .filter(
      (e): e is HasUserRating & { record: { userRating: number } } =>
        typeof e.record.userRating === 'number' &&
        (e.record.userRating as number) >= SEED_MIN_RATING
    )
    .sort((a, b) => (b.record.userRating as number) - (a.record.userRating as number))
    .map((e) => ({ tmdbId: e.tmdbId, userRating: e.record.userRating as number }))
}

